import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  AcademicCapIcon,
  EnvelopeIcon,
  BookOpenIcon,
  StarIcon,
  UserCircleIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import apiService from '../services/api';

const TeacherManagement = () => {
  const [showRegisterTeacher, setShowRegisterTeacher] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const [teachers, setTeachers] = useState([]);

  // Load teachers on component mount
  useEffect(() => {
    loadTeachers();
  }, []);

  const loadTeachers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTeachers();
      
      // Handle paginated response structure
      let teachersData;
      if (response && response.results && Array.isArray(response.results)) {
        // Paginated response
        teachersData = response.results;
      } else if (Array.isArray(response)) {
        // Direct array response
        teachersData = response;
      } else {
        // Fallback to empty array
        teachersData = [];
      }
      
      setTeachers(teachersData);
      setError('');
    } catch (error) {
      console.error('Failed to load teachers:', error);
      setError('Failed to load teachers. Please try again.');
      setTeachers([]); // Ensure teachers is an array even on error
    } finally {
      setLoading(false);
    }
  };

  const [newTeacher, setNewTeacher] = useState({
    full_name: '',
    email: '',
    phone: '',
    specialization: '',
    experience_years: '',
    bio: '',
    send_email: false
  });

  // State for custom specialization
  const [showCustomSpecialization, setShowCustomSpecialization] = useState(false);
  const [customSpecialization, setCustomSpecialization] = useState('');

  const specializations = [
    'Data Science & Machine Learning',
    'Software Engineering',
    'Cybersecurity',
    'Artificial Intelligence',
    'Digital Marketing',
    'UI/UX Design',
    'Cloud Computing',
    'Mobile Development',
    'Other/Custom'
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/60';
      case 'Inactive':
        return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200/60';
      case 'On Leave':
        return 'bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800 border border-yellow-200/60';
      default:
        return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200/60';
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <StarSolid
        key={index}
        className={`h-4 w-4 transition-all duration-300 ${
          index < Math.floor(rating) ? 'text-yellow-400 scale-110' : 'text-gray-300'
        }`}
      />
    ));
  };

  // Handle specialization selection change
  const handleSpecializationChange = (value) => {
    if (value === 'Other/Custom') {
      setShowCustomSpecialization(true);
      setNewTeacher({...newTeacher, specialization: ''});
      setCustomSpecialization('');
    } else {
      setShowCustomSpecialization(false);
      setNewTeacher({...newTeacher, specialization: value});
      setCustomSpecialization('');
    }
  };

  const handleRegisterTeacher = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    // Validate custom specialization if needed
    if (showCustomSpecialization && !customSpecialization.trim()) {
      setError('Please enter a custom specialization.');
      setLoading(false);
      return;
    }
    
    // Prepare teacher data with appropriate specialization
    const teacherData = {
      ...newTeacher,
      specialization: showCustomSpecialization ? customSpecialization.trim() : newTeacher.specialization
    };
    
    try {
      console.log('Registering teacher with data:', teacherData);
      const response = await apiService.createTeacher(teacherData);
      await loadTeachers(); // Reload teachers list
      
      // Reset form
      setNewTeacher({
        full_name: '',
        email: '',
        phone: '',
        specialization: '',
        experience_years: '',
        bio: '',
        send_email: false
      });
      setShowCustomSpecialization(false);
      setCustomSpecialization('');
      setShowRegisterTeacher(false);
      
      // Show success message with login credentials
      if (response.login_credentials) {
        // Show credentials in alert dialog
        const credentialsText = `Teacher Registered Successfully!

Login Credentials:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Email: ${response.login_credentials.email}
Password: ${response.login_credentials.password}
Login URL: ${response.login_credentials.login_url}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Please share these credentials with the teacher securely.`;
        
        alert(credentialsText);
        
        // Also show in success banner
        setSuccess(`Teacher registered successfully! Login credentials generated:
        
Email: ${response.login_credentials.email}
Password: ${response.login_credentials.password}
Login URL: ${response.login_credentials.login_url}

Please share these credentials with the teacher securely.`);
      } else {
        setSuccess('Teacher registered successfully!');
      }
      setError('');
      
      // Clear success message after 30 seconds (longer for credentials)
      setTimeout(() => setSuccess(''), 30000);
    } catch (error) {
      console.error('Failed to register teacher - Full error:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      // Show more specific error message if available
      let errorMessage = 'Failed to register teacher. Please try again.';
      if (error.message) {
        errorMessage = `Registration failed: ${error.message}`;
      }
      
      setError(errorMessage);
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  const copyCredentials = () => {
    // Match both registration and password reset formats
    const credentials = success.match(/Email: ([^\n]+)[\s\S]*?Password: ([^\n]+)[\s\S]*?Login URL: ([^\n]+)/);
    if (credentials && credentials.length === 4) {
      const email = credentials[1].trim();
      const password = credentials[2].trim();
      const loginUrl = credentials[3].trim();
      const textToCopy = `Email: ${email}\nPassword: ${password}\nLogin URL: ${loginUrl}`;
      navigator.clipboard.writeText(textToCopy).then(() => {
        const currentSuccess = success; // Store current message
        setSuccess('Credentials copied to clipboard!');
        setTimeout(() => {
          setSuccess(currentSuccess); // Restore original message
        }, 2000);
      }).catch(err => {
        console.error('Failed to copy credentials:', err);
        const currentSuccess = success;
        setSuccess('Failed to copy credentials.');
        setTimeout(() => {
          setSuccess(currentSuccess);
        }, 2000);
      });
    } else {
      const currentSuccess = success;
      setSuccess('No credentials to copy.');
      setTimeout(() => {
        setSuccess(currentSuccess);
      }, 2000);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">Teacher Management</h1>
          <p className="mt-2 text-gray-600 font-medium">
            Manage teachers, assignments, and performance tracking
          </p>
        </div>
        <button 
          onClick={() => {
            setShowRegisterTeacher(true);
            setShowCustomSpecialization(false);
            setCustomSpecialization('');
          }}
          className="mt-6 sm:mt-0 btn-primary group"
        >
          <PlusIcon className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
          Register Teacher
        </button>
      </div>

      {/* Success/Error Notifications */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 relative">
          <button
            onClick={() => setSuccess('')}
            className="absolute top-2 right-2 text-green-600 hover:text-green-800 transition-colors"
            aria-label="Close"
          >
            <XCircleIcon className="h-5 w-5" />
          </button>
          <div className="flex items-start pr-6">
            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
            <div className="flex-1">
              {(success.includes('Login credentials generated') || success.includes('Password reset successfully')) ? (
                <div>
                  <p className="text-sm font-semibold text-green-800 mb-2">
                    {success.includes('Password reset') ? 'Password Reset Successful!' : 'Teacher registered successfully!'}
                  </p>
                  <div className="bg-white border border-green-200 rounded-lg p-3 mb-2">
                    <p className="text-xs font-semibold text-gray-700 mb-1">Login Credentials:</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-mono text-gray-800">{success.match(/Email: ([^\n]+)/)?.[1] || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Password:</span>
                        <span className="font-mono text-gray-800 font-bold">{success.match(/Password: ([^\n]+)/)?.[1] || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Login URL:</span>
                        <span className="font-mono text-gray-800">{success.match(/Login URL: ([^\n]+)/)?.[1] || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-green-700">Please share these credentials with the teacher securely.</p>
                  <button
                    onClick={copyCredentials}
                    className="mt-3 btn-secondary text-sm"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
                    Copy Credentials
                  </button>
                </div>
              ) : (
                <p className="text-sm text-green-800">{success}</p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="stat-card group">
          <div className="flex items-center">
            <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 shadow-lg group-hover:shadow-xl group-hover:shadow-blue-500/25 transition-all duration-300">
              <AcademicCapIcon className="h-6 w-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-semibold text-gray-600">Total Teachers</p>
              <p className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">{Array.isArray(teachers) ? teachers.length : 0}</p>
            </div>
          </div>
        </div>
        <div className="stat-card group">
          <div className="flex items-center">
            <div className="p-4 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg group-hover:shadow-xl group-hover:shadow-green-500/25 transition-all duration-300">
              <UserCircleIcon className="h-6 w-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-semibold text-gray-600">Active Teachers</p>
              <p className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                {Array.isArray(teachers) ? teachers.filter(t => t.status === 'Active').length : 0}
              </p>
            </div>
          </div>
        </div>
        <div className="stat-card group">
          <div className="flex items-center">
            <div className="p-4 rounded-2xl bg-gradient-to-r from-purple-500 to-violet-600 shadow-lg group-hover:shadow-xl group-hover:shadow-purple-500/25 transition-all duration-300">
              <BookOpenIcon className="h-6 w-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-semibold text-gray-600">Total Modules</p>
              <p className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                {Array.isArray(teachers) ? teachers.reduce((sum, teacher) => sum + (teacher.modules || 0), 0) : 0}
              </p>
            </div>
          </div>
        </div>
        <div className="stat-card group">
          <div className="flex items-center">
            <div className="p-4 rounded-2xl bg-gradient-to-r from-yellow-500 to-orange-600 shadow-lg group-hover:shadow-xl group-hover:shadow-yellow-500/25 transition-all duration-300">
              <StarIcon className="h-6 w-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-semibold text-gray-600">Avg. Rating</p>
              <p className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                {Array.isArray(teachers) && teachers.length > 0 
                  ? (teachers.reduce((sum, teacher) => sum + (teacher.rating || 0), 0) / teachers.length).toFixed(1)
                  : '0.0'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Teachers Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
        {loading ? (
          <div className="col-span-full text-center py-8">
            <div className="flex items-center justify-center">
              <svg className="animate-spin h-8 w-8 text-primary-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading teachers...
            </div>
          </div>
        ) : !Array.isArray(teachers) || teachers.length === 0 ? (
          <div className="col-span-full text-center py-8">
            <div className="text-gray-500">No teachers registered yet. Click "Register Teacher" to get started!</div>
          </div>
        ) : (
          teachers.map((teacher, index) => (
          <div 
            key={teacher.id} 
            className={`bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-xl shadow-black/5 hover:shadow-2xl hover:shadow-black/10 transition-all duration-500 ease-out group hover:scale-[1.02] relative overflow-hidden ${isLoaded ? 'animate-in fade-in slide-in-from-bottom-4' : 'opacity-0'}`}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            {/* Gradient overlay */}
            <div className={`absolute inset-0 bg-gradient-to-br ${teacher.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
            
            {/* Status floating badge */}
            <div className="absolute top-6 right-6">
              <span className={`px-4 py-2 text-xs font-bold rounded-full backdrop-blur-sm shadow-sm ${getStatusColor(teacher.status)}`}>
                {teacher.status}
              </span>
            </div>

            <div className="relative z-10">
              {/* Profile Header */}
              <div className="flex items-start mb-6">
                <div className={`w-16 h-16 bg-gradient-to-r ${teacher.color} rounded-3xl flex items-center justify-center shadow-xl group-hover:shadow-2xl group-hover:scale-110 transition-all duration-300 mr-4`}>
                  <span className="text-white font-bold text-xl">{teacher.avatar}</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-gray-900 group-hover:to-gray-600 group-hover:bg-clip-text transition-all duration-300 mb-1">
                    {teacher.name}
                  </h3>
                  <p className="text-gray-600 font-medium mb-2">{teacher.specialization}</p>
                  <div className="flex items-center">
                    <div className="flex mr-2">
                      {renderStars(teacher.rating)}
                    </div>
                    <span className="font-bold text-yellow-600">{teacher.rating}</span>
                  </div>
                </div>
              </div>

              {/* Bio */}
              <p className="text-gray-600 text-sm leading-relaxed mb-6 bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-gray-200/40">{teacher.bio}</p>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-gradient-to-br from-gray-50/80 to-white/60 backdrop-blur-sm rounded-2xl border border-gray-200/40">
                  <p className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    {teacher.students || 0}
                  </p>
                  <p className="text-xs font-semibold text-gray-600 mt-1">Students</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-gray-50/80 to-white/60 backdrop-blur-sm rounded-2xl border border-gray-200/40">
                  <p className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    {teacher.modules || 0}
                  </p>
                  <p className="text-xs font-semibold text-gray-600 mt-1">Modules</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-gray-50/80 to-white/60 backdrop-blur-sm rounded-2xl border border-gray-200/40">
                  <p className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                    {teacher.experience_years ? `${teacher.experience_years} years` : 'N/A'}
                  </p>
                  <p className="text-xs font-semibold text-gray-600 mt-1">Experience</p>
                </div>
              </div>

              {/* Courses Teaching */}
              <div className="mb-6">
                <p className="text-gray-700 font-semibold mb-3">Courses Teaching:</p>
                <div className="flex flex-wrap gap-2">
                  {Array.isArray(teacher.courses) && teacher.courses.length > 0 ? (
                    teacher.courses.map((course, index) => (
                      <span
                        key={index}
                        className="inline-flex px-3 py-2 text-xs font-bold bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 rounded-full border border-blue-200/60 backdrop-blur-sm"
                      >
                        {course}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">No courses assigned</span>
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div className="mb-8 p-4 bg-white/60 backdrop-blur-sm rounded-2xl border border-gray-200/40">
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <EnvelopeIcon className="h-4 w-4 mr-2 text-indigo-500" />
                  <span className="font-medium truncate">{teacher.email}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <span className="font-medium">Joined: {teacher.joinDate}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button className="flex-1 bg-white/80 backdrop-blur-sm border border-gray-200/60 text-gray-700 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50/80 font-semibold py-3 px-4 rounded-2xl transition-all duration-300 group/btn flex items-center justify-center">
                  <EyeIcon className="h-4 w-4 mr-2 group-hover/btn:scale-110 transition-transform duration-200" />
                  View Profile
                </button>
                <button 
                  onClick={async () => {
                    try {
                      setLoading(true);
                      setError('');
                      setSuccess('');
                      const response = await apiService.resetTeacherPassword(teacher.id, { send_email: true });
                      if (response.login_credentials) {
                        const emailStatus = response.email_sent ? ' and sent via email' : '';
                        
                        // Show credentials in alert dialog
                        // Ensure password has no extra whitespace
                        const cleanPassword = String(response.login_credentials.password).trim();
                        const credentialsText = `Password Reset Successful${emailStatus}!

New Login Credentials:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Email: ${response.login_credentials.email}
Password: ${cleanPassword}
Login URL: ${response.login_credentials.login_url}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

⚠️ IMPORTANT: Copy the password carefully - no spaces before or after!
Please share these new credentials with the teacher securely.`;
                        
                        alert(credentialsText);
                        
                        // Also show in success banner (use cleaned password)
                        setSuccess(`Password reset successfully${emailStatus}! New login credentials:\n\nEmail: ${response.login_credentials.email}\nPassword: ${cleanPassword}\nLogin URL: ${response.login_credentials.login_url}\n\nPlease share these new credentials with the teacher securely.`);
                        
                        // Don't auto-clear password reset messages - let user manually dismiss
                        // They need to see the credentials clearly
                      }
                    } catch (error) {
                      setError('Failed to reset password. Please try again.');
                      setTimeout(() => setError(''), 5000);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="flex-1 bg-white/80 backdrop-blur-sm border border-gray-200/60 text-gray-700 hover:text-orange-600 hover:border-orange-300 hover:bg-orange-50/80 font-semibold py-3 px-4 rounded-2xl transition-all duration-300 group/btn flex items-center justify-center"
                >
                  <ClipboardDocumentIcon className="h-4 w-4 mr-2 group-hover/btn:scale-110 transition-transform duration-200" />
                  Reset Password
                </button>
              </div>
            </div>

            {/* Floating indicators */}
            <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
          ))
        )}
      </div>

      {/* Register Teacher Modal */}
      {showRegisterTeacher && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Register New Teacher</h3>
              <button
                onClick={() => {
                  setShowRegisterTeacher(false);
                  setShowCustomSpecialization(false);
                  setCustomSpecialization('');
                }}
                className="text-gray-400 hover:text-gray-600"
                disabled={loading}
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>
            
            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                  <p className="text-sm text-green-800">{success}</p>
                </div>
              </div>
            )}
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <XCircleIcon className="h-4 w-4 text-red-500 mr-2" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}
            
            <form onSubmit={handleRegisterTeacher} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    required
                    value={newTeacher.full_name}
                    onChange={(e) => setNewTeacher({...newTeacher, full_name: e.target.value})}
                    className="input-field"
                    placeholder="Dr. John Smith"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={newTeacher.email}
                    onChange={(e) => setNewTeacher({...newTeacher, email: e.target.value})}
                    className="input-field"
                    placeholder="john.smith@aitech.com"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    required
                    value={newTeacher.phone}
                    onChange={(e) => setNewTeacher({...newTeacher, phone: e.target.value})}
                    className="input-field"
                    placeholder="+234 801 234 5678"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Experience (Years)</label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={newTeacher.experience_years}
                    onChange={(e) => setNewTeacher({...newTeacher, experience_years: e.target.value})}
                    className="input-field"
                    placeholder="5"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Specialization</label>
                <select
                  required={!showCustomSpecialization}
                  value={showCustomSpecialization ? 'Other/Custom' : newTeacher.specialization}
                  onChange={(e) => handleSpecializationChange(e.target.value)}
                  className="input-field"
                  disabled={loading}
                >
                  <option value="">Select Specialization</option>
                  {specializations.map((spec) => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
                </select>
                
                {/* Custom Specialization Input */}
                {showCustomSpecialization && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Specialization <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={customSpecialization}
                      onChange={(e) => setCustomSpecialization(e.target.value)}
                      className="input-field"
                      placeholder="Enter your custom specialization..."
                      disabled={loading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Please enter your specific area of expertise if it's not listed above.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <textarea
                  required
                  value={newTeacher.bio}
                  onChange={(e) => setNewTeacher({...newTeacher, bio: e.target.value})}
                  className="input-field"
                  rows="4"
                  placeholder="Brief professional background and expertise..."
                  disabled={loading}
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Qualifications</label>
                <textarea
                  className="input-field"
                  rows="3"
                  placeholder="Educational background, certifications, etc."
                  disabled={loading}
                ></textarea>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="send_email"
                  checked={newTeacher.send_email}
                  onChange={(e) => setNewTeacher({...newTeacher, send_email: e.target.checked})}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  disabled={loading}
                />
                <label htmlFor="send_email" className="ml-2 block text-sm text-gray-700">
                  Send login credentials via email to the teacher
                </label>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button 
                  type="submit" 
                  className="flex-1 btn-primary disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Registering...' : 'Register Teacher'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRegisterTeacher(false);
                    setShowCustomSpecialization(false);
                    setCustomSpecialization('');
                  }}
                  className="flex-1 btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherManagement; 